import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

function AnimatedSection({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {children}
    </div>
  );
}

const values = [
  {
    icon: "ri-heart-3-line",
    title: "Kasih Sayang",
    desc: "Kami percaya setiap manusia berhak mendapat kasih sayang dan bantuan tanpa mengira bangsa atau status.",
  },
  {
    icon: "ri-hand-heart-line",
    title: "Keikhlasan",
    desc: "Setiap tindakan kami didorong oleh niat yang suci dan keikhlasan untuk membantu tanpa mengharapkan balasan.",
  },
  {
    icon: "ri-shield-star-line",
    title: "Amanah",
    desc: "Kami memastikan setiap sumbangan diagihkan dengan telus dan bertanggungjawab kepada yang berhak.",
  },
  {
    icon: "ri-lightbulb-line",
    title: "Inovasi",
    desc: "Kami sentiasa mencari cara baru yang lebih berkesan untuk memberi bantuan dan memperkasakan komuniti.",
  },
];

export default function About() {
  return (
    <div className="min-h-screen pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[400px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20candid%20photo%20of%20diverse%20Malaysian%20volunteers%20working%20together%20at%20a%20charity%20event%2C%20packing%20food%20aid%20boxes%2C%20warm%20natural%20lighting%2C%20earth%20tone%20colors%2C%20green%20and%20beige%20palette%2C%20joyful%20atmosphere%2C%20humanitarian%20photography&width=1600&height=500&seq=abouthero&orientation=landscape"
          alt="Sukarelawan Mulia Insan"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-teal-900/70" />
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white">
            Tentang Kami
          </h1>
          <p className="text-white/80 text-sm md:text-base mt-3 max-w-xl">
            Mengenali visi, misi, dan pasukan di sebalik PERTUBUHAN MULIA INSAN
            MALAYSIA
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
                Cerita Kami
              </p>
              <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900 mb-5">
                Bermula dari Hati,
                <br />
                <span className="font-light text-gray-500">
                  Berbuah dengan Kasih
                </span>
              </h2>
              <div className="space-y-4 text-sm md:text-base text-gray-600 leading-relaxed">
                <p>
                  <strong>PERTUBUHAN MULIA INSAN MALAYSIA</strong> (No. Pendaftaran: <strong>PPM-009-10-25092024</strong>) ditubuhkan pada tahun <strong>2024</strong> dengan satu misi
                  mulia &mdash; membantu mereka yang terpinggir di masyarakat.
                  Bermula dengan usaha kecil menyalurkan bantuan makanan kepada
                  keluarga miskin di pinggir bandar, kami telah berkembang
                  menjadi organisasi amal yang menyentuh ribuan kehidupan di
                  seluruh Malaysia.
                </p>
                <p>
                  Kami fokus pada empat tonggak utama: <strong>makanan</strong>,{" "}
                  <strong>pendidikan</strong>, <strong>perlindungan</strong>, dan{" "}
                  <strong>kesihatan</strong>. Setiap program direka untuk
                  memberi impak berterusan, bukan hanya bantuan sementara.
                </p>
                <p>
                  Di samping itu, kami juga melancarkan program Latihan AI &amp;
                  Komputer untuk memperkasakan golongan miskin dan pelarian
                  dengan kemahiran digital moden &mdash; membuka pintu kepada
                  peluang ekonomi yang lebih baik.
                </p>
              </div>
              <Link
                to="/programs"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800 transition-colors mt-6"
              >
                Lihat Program Kami <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://readdy.ai/api/search-image?query=Malaysian%20volunteers%20distributing%20food%20packages%20to%20families%20in%20need%20outdoor%20community%20setting%20warm%20golden%20hour%20lighting%20earth%20tones%20emotional%20humanitarian%20photography&width=400&height=500&seq=abt1&orientation=portrait"
                alt="Sukarelawan mengedarkan bantuan"
                className="w-full rounded-xl object-cover h-48 md:h-64"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Malaysian%20children%20from%20diverse%20backgrounds%20studying%20together%20in%20a%20community%20learning%20center%20warm%20lighting%20books%20and%20laptops%20earth%20tone%20palette%20hopeful%20atmosphere&width=400&height=500&seq=abt2&orientation=portrait"
                alt="Kanak-kanak belajar bersama"
                className="w-full rounded-xl object-cover h-48 md:h-64 mt-6"
              />
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Vision & Mission */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-earth-50">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-50 mb-4">
                <i className="ri-eye-line text-teal-600 text-xl" />
              </div>
              <h3 className="font-display text-xl md:text-2xl font-bold text-gray-900 mb-3">
                Visi
              </h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Menjadi organisasi amal terkemuka di Malaysia yang memastikan
                setiap individu &mdash; tanpa mengira latar belakang &mdash; mempunyai
                akses kepada keperluan asas dan peluang untuk membangun kehidupan
                yang bermaruah.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 mb-4">
                <i className="ri-focus-3-line text-primary-600 text-xl" />
              </div>
              <h3 className="font-display text-xl md:text-2xl font-bold text-gray-900 mb-3">
                Misi
              </h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Memberi bantuan berterusan dalam bentuk makanan, pendidikan,
                perlindungan, dan kesihatan kepada golongan yang memerlukan.
                Memperkasakan komuniti dengan kemahiran digital melalui program
                latihan AI dan komputer.
              </p>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Values */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
              Nilai Kami
            </p>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900">
              Prinsip Yang Memandu Kami
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {values.map((v) => (
              <div
                key={v.title}
                className="rounded-xl border border-gray-100 p-5 md:p-6 hover:shadow-sm transition-shadow"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-teal-50 mb-4">
                  <i className={`${v.icon} text-teal-600 text-lg`} />
                </div>
                <h3 className="font-semibold text-sm md:text-base text-gray-900 mb-2">
                  {v.title}
                </h3>
                <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                  {v.desc}
                </p>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </section>

      {/* Team */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-earth-100">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
              Pasukan Kami
            </p>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900">
              Bertemu Wajah-Wajah Mulia Insan
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                name: "Dato\u2019 Ahmad Faizal",
                role: "Pengerusi",
                img: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20distinguished%20Malaysian%20man%20in%20his%2050s%20wearing%20a%20dark%20suit%20warm%20lighting%20simple%20background%20confident%20smile&width=400&height=450&seq=team1&orientation=portrait",
              },
              {
                name: "Pn. Siti Norzaharah",
                role: "Pengarah Eksekutif",
                img: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20Malaysian%20woman%20in%20her%2040s%20wearing%20a%20teal%20blouse%20warm%20lighting%20simple%20background%20friendly%20smile&width=400&height=450&seq=team2&orientation=portrait",
              },
              {
                name: "Encik Muhammad Hafiz",
                role: "Pengarah Program",
                img: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20young%20Malaysian%20man%20in%20his%20early%2030s%20wearing%20a%20casual%20shirt%20warm%20lighting%20simple%20background%20cheerful%20smile&width=400&height=450&seq=team3&orientation=portrait",
              },
            ].map((member) => (
              <div
                key={member.name}
                className="bg-white rounded-xl overflow-hidden border border-gray-100"
              >
                <img
                  src={member.img}
                  alt={member.name}
                  className="w-full h-56 md:h-64 object-cover"
                />
                <div className="p-4 md:p-5 text-center">
                  <h3 className="font-semibold text-sm md:text-base text-gray-900">
                    {member.name}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 mt-0.5">
                    {member.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
}